import React, { useEffect, useRef, useState } from "react";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import "./PlantationOneCounter.scss";

const PlantationOneCounter = () => {
  const [counterData, setCounterData] = useState([]);
  const { document: homeCounter, error } = useDocument("counters", "homeCounter");
  const counterRefs = useRef([]);

  useEffect(() => {
    if (homeCounter) {
      const extractedData = [
        homeCounter.firstCounter,
        homeCounter.secondCounter,
        homeCounter.thirdCounter,
        homeCounter.fourthCounter,
      ].filter((counter) => counter && counter.name && counter.number);

      const sortedData = extractedData.sort((a, b) => {
        const numA = parseFloat(a.number.toString().replace(/[^0-9.]/g, ""));
        const numB = parseFloat(b.number.toString().replace(/[^0-9.]/g, ""));
        return numA - numB;
      });

      setCounterData(sortedData);
    }
  }, [homeCounter]);

  useEffect(() => {
    if (counterData.length === 0) return;

    const animateCounters = () => {
      counterRefs.current.forEach((counter, index) => {
        if (!counter || index >= counterData.length) return;

        const targetString = counterData[index].number.toString();
        const numericValue = parseFloat(targetString.replace(/[^0-9.]/g, ""));
        const hasPlusSuffix = targetString.endsWith('+');
        const duration = 2000;
        const startTime = performance.now();

        const updateCounter = (currentTime) => {
          const elapsedTime = currentTime - startTime;
          const progress = Math.min(elapsedTime / duration, 1);
          const currentValue = Math.floor(progress * numericValue);

          const displayValue = hasPlusSuffix
            ? `${currentValue}+`
            : `${currentValue}`;

          counter.innerHTML = displayValue;

          if (progress < 1) {
            requestAnimationFrame(updateCounter);
          }
        };

        requestAnimationFrame(updateCounter);
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    const counterElement = document.querySelector(".nature-counter");
    if (counterElement) {
      observer.observe(counterElement);
    }

    return () => observer.disconnect();
  }, [counterData]);

  if (error) {
    return <div className="error">Error loading counters: {error}</div>;
  }

  if (!homeCounter) {
    return <div className="loading">Loading counters...</div>;
  }

  return (
    <section className="nature-counter nature-section">
      <div className="nature-container">
        <div className="counter-content">
          <div className="counter-header">
            <p className="nature-subheading">
              <span>OUR IMPACT</span>
              <div className="nature-subheading__underline nature-subheading__underline--left"></div>
            </p>

            <h2 className="nature-heading">Creating Lasting Change</h2>

            <p className="nature-text">
              Our measurable impact on the environment and communities we serve.
            </p>
          </div>

          <div className="counter-grid">
            {counterData.map((item, index) => (
              <div className="counter-item" key={index}>
                <div
                  className="counter-icon"
                  dangerouslySetInnerHTML={{ __html: item.iconName }}
                />
                <div className="counter-number">
                  <span
                    className="count"
                    ref={(el) => (counterRefs.current[index] = el)}
                  >
                    {item.number.toString().endsWith('+') ? '0+' : '0'}
                  </span>
                </div>
                <div className="counter-label">{item.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlantationOneCounter;
